import { NodeApi, Tree } from 'react-arborist';
import { Flex, Box, Checkbox, Text } from '@chakra-ui/react';
import { ChevronDownIcon, ChevronRightIcon } from '@chakra-ui/icons';
import { useEffect, useState } from 'react';

export interface ITreeProps {
    checked: boolean;
}

const TreeView = ({
    treeData,
    childrenAccessor,
    onChange,
    disabled,
    childrenDisabled,
}: {
    treeData: any;
    childrenAccessor: string;
    onChange?: any;
    disabled?: boolean;
    childrenDisabled?: boolean;
}) => {
    const [selectTree, setSelectTreeOptions] = useState<any[]>();
    const [accessor, setAccessorOptions] = useState<string>(childrenAccessor.length ? childrenAccessor : 'children');

    const checkboxContainerStyles = {
        minWidth: '36px',
    };

    const copyTree = (data: any[]) => {
        return JSON.parse(JSON.stringify(data));
    };

    useEffect(() => {
        if (!selectTree?.length) {
            setSelectTreeOptions(copyTree(treeData));
        }
    }, [treeData]);

    const toogleElem = (tree: any, value: boolean) => {
        const newTree = { ...tree };
        newTree.isChecked = value;
        if (newTree.children && newTree.children.length) {
            newTree.children = newTree.children.map((child: any) => toogleElem(child, value));
        }
        return newTree;
    };

    const editElem = (tree: any, node: NodeApi<any>, value: boolean): any => {
        const newTree = { ...tree };
        if (newTree.id === node.id) {
            newTree.isChecked = value;
            if (newTree.children && newTree.children.length) {
                newTree.children = newTree.children.map((child: any) => toogleElem(child, value));
            }
        } else if (newTree.children) {
            newTree.children = newTree.children.map((child: any) => editElem(child, node, value));
        }

        return newTree;
    };

    const searchElem = (tree: any[], node: NodeApi<any>, level: number): any => {
        return tree.map((elem: any) => {
            if (node.level > level && elem.children && elem.children.length) {
                return searchElem(elem.children, node, level + 1).filter((n: any) => n);
            } else if (node.level === level && elem.id == node.id) {
                return elem;
            }
        });
    };

    const onTreeSelect = (node: NodeApi<any>, level: number) => (event: React.ChangeEvent<HTMLInputElement>) => {
        if (selectTree) {
            const found = searchElem(selectTree, node, 0)
                .flat(Infinity)
                .filter((n: any) => n)[0];

            const newTree = [...selectTree];
            const edited = newTree.map((elem) => editElem(elem, node, !found.isChecked));

            setSelectTreeOptions(edited);
            if (onChange) {
                onChange(node, edited);
            }
        }
    };

    const getNodeValue = (node: NodeApi<any>) => {
        if (selectTree) {
            return searchElem(selectTree, node, 0)
                .flat(Infinity)
                .filter((n: any) => n)[0];
        }

        return { isChecked: false };
    };

    const isIndeterminate = (node: NodeApi<any>) => {
        if (selectTree && node.children?.length) {
            const checkedItems = searchElem(selectTree, node, 0)
                .flat(Infinity)
                .filter((n: any) => n)[0]
                .children.map((d: any) => d.isChecked);

            const allChecked = checkedItems.every(Boolean);
            const isIndeterminate = checkedItems.some(Boolean) && !allChecked;

            return { isIndeterminate: isIndeterminate };
        }

        return { isIndeterminate: false };
    };

    return (
        <Tree
            initialData={treeData}
            childrenAccessor={accessor}
            openByDefault={false}
            key={'open'}
            width={'100%'}
            height={250}
            indent={15}
            rowHeight={33}
        >
            {({ node, style }: { node: NodeApi<any>; style: React.CSSProperties }) => {
                const iconParams = {
                    onClick: () => {
                        if (node.isOpen) {
                            node.close();
                        } else {
                            node.open();
                        }
                    },

                    style: {
                        cursor: 'pointer',
                        minWidth: '20px',
                    },
                };

                let NodeIcon;
                if (!node.children || !node?.children?.length) {
                    NodeIcon = <Box minWidth="20px"></Box>;
                } else if (node.isOpen) {
                    NodeIcon = <ChevronDownIcon {...iconParams} />;
                } else {
                    NodeIcon = <ChevronRightIcon {...iconParams} />;
                }
                return (
                    <Flex style={style} alignItems="center">
                        {NodeIcon}
                        <Flex alignItems="center" {...checkboxContainerStyles}>
                            <Checkbox
                                ml="10px"
                                mr="10px"
                                type="checkbox"
                                isDisabled={disabled || (node?.level !== 0 && childrenDisabled)}
                                onChange={onTreeSelect(node, 0)}
                                isIndeterminate={isIndeterminate(node).isIndeterminate}
                                isChecked={getNodeValue(node)?.isChecked}
                            />
                        </Flex>

                        <Text fontSize={'sm'}>{node.data.name}</Text>
                    </Flex>
                );
            }}
        </Tree>
    );
};

export default TreeView;
